import React, { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Descriptions,
    Drawer,
    Form,
    Input,
    Modal,
    Radio,
    Select,
    Space,
    Table,
    Tag,
    Upload,
    message,
} from 'antd';
import {
    DeleteOutlined,
    EditOutlined,
    EyeOutlined,
    FileAddOutlined,
    MailOutlined,
    MinusCircleOutlined,
    PlusOutlined,
    ReloadOutlined,
    UploadOutlined,
} from '@ant-design/icons';
import PageHeader from '../../components/business/PageHeader';
import {
    EmailTemplatePreview,
    EmailTemplateReference,
    EmailTestRecipient,
    MailAccount,
    RecipientListOwner,
} from '../../models/mail';
import { PageResult } from '../../models/v2';
import { v2Api } from '../../services/v2';
import { asV2Error, formatDateTime } from '../../utils/v2';
import {
    blockRemoteImages,
    templateAnalysisStatusLabel,
    templateTestStatusLabel,
    toIsoDateTime,
} from './mailUi';
import { MAIL_PAGE_SIZE_OPTIONS, readMailPageSize, rememberMailPageSize } from './mailPagination';
import MailHtmlEditor from './MailHtmlEditor';

const ownerRoleLabel = (roleCodes?: string) =>
    (roleCodes || '').replace(/SUPER_ADMIN/g, '超级管理员').replace(/ADMIN/g, '管理员').replace(/SALES/g, '销售');

const templateCheckMessage = (issue: string) => {
    if (issue === 'SANITIZED_HTML_TOO_LARGE') return '清洗后的 HTML 超过大小限制';
    if (issue === 'IMAGE_ALT_MISSING') return '图片缺少 alt 替代文字';
    if (issue.startsWith('BLOCKED_TAG:')) return `包含不允许的标签 <${issue.substring('BLOCKED_TAG:'.length)}>`;
    if (issue.startsWith('EVENT_ATTRIBUTE:')) return `包含事件属性 ${issue.substring('EVENT_ATTRIBUTE:'.length)}`;
    if (issue.startsWith('UNSAFE_URL:')) return `包含不安全链接 ${issue.substring('UNSAFE_URL:'.length)}`;
    if (issue.startsWith('INSECURE_HTTP_URL:')) return `链接建议升级为 HTTPS：${issue.substring('INSECURE_HTTP_URL:'.length)}`;
    if (issue === 'UNSAFE_STYLE_BLOCK_REMOVED') return '包含外部资源或危险语法的样式块已自动移除';
    if (issue.startsWith('UNKNOWN_VARIABLE:')) return `包含未知变量 ${issue.substring('UNKNOWN_VARIABLE:'.length)}`;
    return '其他安全检查项';
};

const sanitizationReport = (value: EmailTemplatePreview | null) => {
    if (!value) return undefined;
    const report = value.sanitizationReportJson;
    if (typeof report !== 'string') return report;
    try {
        return JSON.parse(report);
    } catch (_) {
        return undefined;
    }
};

const dateTimeLocalValue = (value?: string | number | number[]) => {
    if (!value) return undefined;
    if (Array.isArray(value)) {
        const [year, month, day, hour = 0, minute = 0, second = 0] = value;
        const pad = (part: number) => String(part).padStart(2, '0');
        return `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}:${pad(second)}`;
    }
    const text = String(value).replace(' ', 'T');
    return text.length >= 19 ? text.slice(0, 19) : text;
};

const readHtmlFile = (file: File) =>
    new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(new Error('无法读取 HTML 文件'));
        reader.readAsText(file, 'UTF-8');
    });

const normalizedAssetPath = (value: string) => {
    const withoutQuery = value.split('#')[0].split('?')[0];
    let decoded = withoutQuery;
    try {
        decoded = decodeURIComponent(withoutQuery);
    } catch (_) {
        // Keep the original path when it contains an invalid escape sequence.
    }
    return decoded.replace(/\\/g, '/').replace(/^file:\/+/i, '').replace(/^\.\//, '').replace(/^\//, '');
};

const imageExtension = (contentType: string) => {
    if (contentType.toLowerCase() === 'image/png') return 'png';
    if (contentType.toLowerCase() === 'image/gif') return 'gif';
    return 'jpg';
};

const dataImageFile = (source: string, index: number) => {
    const match = source.match(/^data:(image\/(?:png|jpeg|jpg|gif));base64,([\s\S]+)$/i);
    if (!match) throw new Error(`第 ${index + 1} 张内嵌图片格式不受支持，仅支持 PNG、JPEG 和 GIF`);
    try {
        const binary = window.atob(match[2].replace(/\s/g, ''));
        const bytes = new Uint8Array(binary.length);
        for (let offset = 0; offset < binary.length; offset += 1) bytes[offset] = binary.charCodeAt(offset);
        const contentType = match[1].toLowerCase().replace('image/jpg', 'image/jpeg');
        return new File([bytes], `html-image-${index + 1}.${imageExtension(contentType)}`, { type: contentType });
    } catch (_) {
        throw new Error(`第 ${index + 1} 张内嵌图片数据已损坏`);
    }
};

const findReferencedImage = (source: string, files: File[]) => {
    const sourcePath = normalizedAssetPath(source);
    const sourceName = sourcePath.substring(sourcePath.lastIndexOf('/') + 1).toLowerCase();
    const exact = files.filter((candidate) => {
        const relativePath = (candidate as File & { webkitRelativePath?: string }).webkitRelativePath;
        return normalizedAssetPath(relativePath || candidate.name).toLowerCase() === sourcePath.toLowerCase();
    });
    if (exact.length === 1) return exact[0];
    const byName = files.filter((candidate) => candidate.name.toLowerCase() === sourceName);
    return byName.length === 1 ? byName[0] : undefined;
};

const materializeHtmlImages = async (
    html: string,
    referencedFiles: File[],
    upload: (file: File) => Promise<string>
) => {
    const document = new DOMParser().parseFromString(html, 'text/html');
    const images = Array.from(document.querySelectorAll('img[src]')) as HTMLImageElement[];
    const pending = new Map<string, { file: File; elements: HTMLImageElement[] }>();

    images.forEach((element, index) => {
        const source = (element.getAttribute('src') || '').trim();
        if (!source || /^https?:\/\//i.test(source)) return;
        if (/^blob:/i.test(source)) {
            throw new Error('HTML 中包含临时 blob 图片，请使用“上传并插入图片”重新选择该图片');
        }
        const file = /^data:/i.test(source)
            ? dataImageFile(source, index)
            : findReferencedImage(source, referencedFiles);
        if (!file) {
            throw new Error(`未找到 HTML 引用的本地图片：${source}，请在“关联图片”中选择该文件`);
        }
        const key = /^data:/i.test(source) ? source : `${normalizedAssetPath(source)}:${file.name}`;
        const item = pending.get(key);
        if (item) item.elements.push(element);
        else pending.set(key, { file, elements: [element] });
    });

    await Promise.all(Array.from(pending.values()).map(async (item) => {
        const url = await upload(item.file);
        item.elements.forEach((element) => element.setAttribute('src', url));
    }));

    return {
        html: `<!doctype html>${document.documentElement.outerHTML}`,
        uploadedCount: pending.size,
    };
};

const EmailTemplatesPage = () => {
    const [createForm] = Form.useForm();
    const [editForm] = Form.useForm();
    const [uploadForm] = Form.useForm();
    const [testRecipientForm] = Form.useForm();
    const [testSendForm] = Form.useForm();
    const [templates, setTemplates] = useState<PageResult<EmailTemplateReference>>({
        items: [],
        pageNo: 1,
        pageSize: readMailPageSize('templates', 20),
        total: 0,
    });
    const [keyword, setKeyword] = useState('');
    const [listLoading, setListLoading] = useState(false);
    const [current, setCurrent] = useState<EmailTemplateReference | null>(null);
    const [preview, setPreview] = useState<EmailTemplatePreview | null>(null);
    const [mode, setMode] = useState<'desktop' | 'mobile' | 'text'>('desktop');
    const [file, setFile] = useState<File | null>(null);
    const [editFile, setEditFile] = useState<File | null>(null);
    const [uploadImageFiles, setUploadImageFiles] = useState<File[]>([]);
    const [editImageFiles, setEditImageFiles] = useState<File[]>([]);
    const [uploadHtmlBody, setUploadHtmlBody] = useState('');
    const [editHtmlBody, setEditHtmlBody] = useState('');
    const [createVisible, setCreateVisible] = useState(false);
    const [editVisible, setEditVisible] = useState(false);
    const [uploadVisible, setUploadVisible] = useState(false);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [testRecipientVisible, setTestRecipientVisible] = useState(false);
    const [testSendVisible, setTestSendVisible] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [loadRemoteImages, setLoadRemoteImages] = useState(false);
    const [ownerOptions, setOwnerOptions] = useState<RecipientListOwner[]>([]);
    const [loadingOwners, setLoadingOwners] = useState(false);
    const [ownerError, setOwnerError] = useState<string>();
    const [canViewAllOwners, setCanViewAllOwners] = useState(false);
    const [currentActorId, setCurrentActorId] = useState<number>();
    const [selectedOwnerId, setSelectedOwnerId] = useState<number>();
    const [testRecipients, setTestRecipients] = useState<EmailTestRecipient[]>([]);
    const [testMailAccounts, setTestMailAccounts] = useState<MailAccount[]>([]);

    const loadTemplates = async (pageNo = 1, pageSize = templates.pageSize, search = keyword) => {
        setListLoading(true);
        try {
            const result = await v2Api.emailContents({
                ownerId: selectedOwnerId,
                keyword: search.trim() || undefined,
                pageNo,
                pageSize,
            });
            setTemplates(result);
        } catch (error) {
            message.error(asV2Error(error).message);
        } finally {
            setListLoading(false);
        }
    };

    useEffect(() => {
        const loadInitialTemplates = async () => {
            setListLoading(true);
            try {
                const owners = await v2Api.recipientListOwners();
                const ownerId = owners.currentActorId;
                setOwnerOptions(owners.items || []);
                setCanViewAllOwners(owners.canViewAll);
                setCurrentActorId(owners.currentActorId);
                setSelectedOwnerId(ownerId);
                setTemplates(await v2Api.emailContents({ ownerId, pageNo: 1, pageSize: readMailPageSize('templates', 20) }));
            } catch (error) {
                message.error(asV2Error(error).message);
            } finally {
                setListLoading(false);
            }
        };
        loadInitialTemplates();
        const query = new URLSearchParams(window.location.hash.split('?')[1] || '');
        const contentId = Number(query.get('contentId'));
        const versionId = Number(query.get('versionId'));
        if (contentId > 0) {
            const ref = { contentId, versionId: versionId || undefined };
            setCurrent(ref);
            if (versionId > 0) openPreview(ref, 'desktop');
        }
    }, []);

    const loadOwnerOptions = async () => {
        if (loadingOwners || ownerOptions.length > 0) return;
        setLoadingOwners(true);
        setOwnerError(undefined);
        try {
            const response = await v2Api.recipientListOwners();
            const eligible = response.items || [];
            setOwnerOptions(eligible);
            setCanViewAllOwners(response.canViewAll);
            setCurrentActorId(response.currentActorId);
            if (!createForm.getFieldValue('ownerId')) {
                createForm.setFieldsValue({ ownerId: response.currentActorId });
            }
            if (eligible.length === 0) setOwnerError('没有找到启用中的销售或管理员账号');
        } catch (error) {
            setOwnerError('负责人列表加载失败，请稍后重试');
        } finally {
            setLoadingOwners(false);
        }
    };

    const createTemplate = async () => {
        const values = await createForm.validateFields();
        setSaving(true);
        try {
            const result = await v2Api.createEmailContent({
                contentName: values.contentName,
                businessDomainCode: values.businessDomainCode,
                ownerId: Number(values.ownerId),
            });
            const ref: EmailTemplateReference = {
                contentId: result.id,
                contentName: values.contentName,
                updatedAt: new Date().toISOString(),
            };
            setCurrent(ref);
            setCreateVisible(false);
            createForm.resetFields();
            uploadForm.setFieldsValue({ senderDisplayName: 'SynRay Consulting' });
            setUploadHtmlBody('');
            setUploadImageFiles([]);
            setUploadVisible(true);
            loadTemplates(1, templates.pageSize, keyword);
            message.success('模板主档已创建，请编辑邮件内容');
        } catch (error) {
            message.error(asV2Error(error).message);
        } finally {
            setSaving(false);
        }
    };

    const uploadHtml = async () => {
        if (!current) return;
        if (!uploadHtmlBody.trim()) {
            message.error('请上传 HTML 文件或在编辑器中填写邮件正文');
            return;
        }
        const values = await uploadForm.validateFields();
        setSaving(true);
        try {
            const prepared = await materializeHtmlImages(
                uploadHtmlBody,
                uploadImageFiles,
                async (image) => (await v2Api.uploadEmailTemplateImage(image)).url
            );
            const result = await v2Api.createEmailContentVersion(current.contentId, {
                htmlBody: prepared.html,
                subjectText: values.subjectText,
                previewText: values.previewText,
                senderDisplayName: values.senderDisplayName,
                replyToAddress: values.replyToAddress,
                validUntil: toIsoDateTime(values.validUntil),
            });
            const ref = {
                ...current,
                ...result,
                contentName: current.contentName,
                updatedAt: new Date().toISOString(),
            };
            setCurrent(ref);
            setUploadVisible(false);
            setFile(null);
            setUploadHtmlBody('');
            setUploadImageFiles([]);
            uploadForm.resetFields();
            loadTemplates(templates.pageNo, templates.pageSize, keyword);
            if (result.analysisStatus === 'READY') {
                message.success(prepared.uploadedCount > 0
                    ? `已上传并替换 ${prepared.uploadedCount} 张图片，HTML 已创建安全草稿`
                    : 'HTML 已清洗并创建安全草稿');
            } else {
                const blockers = (result.blockers || []).map(templateCheckMessage);
                message.warning(
                    blockers.length > 0
                        ? `HTML 已导入，安全检查未通过：${blockers.join('；')}`
                        : 'HTML 已导入，但存在阻断项'
                );
            }
            if (ref.versionId) openPreview(ref, 'desktop');
        } catch (error) {
            message.error(asV2Error(error).message);
        } finally {
            setSaving(false);
        }
    };

    const openPreview = async (
        ref: EmailTemplateReference,
        nextMode: 'desktop' | 'mobile' | 'text'
    ) => {
        if (!ref.versionId) {
            message.info('请先创建 HTML 版本');
            return;
        }
        setCurrent(ref);
        setMode(nextMode);
        setLoadRemoteImages(false);
        setPreviewVisible(true);
        setLoadingPreview(true);
        try {
            setPreview(await v2Api.emailContentPreview(ref.contentId, ref.versionId, nextMode));
        } catch (error) {
            message.error(asV2Error(error).message);
            setPreview(null);
        } finally {
            setLoadingPreview(false);
        }
    };

    const changePreviewMode = (nextMode: 'desktop' | 'mobile' | 'text') => {
        if (current) openPreview(current, nextMode);
    };

    const openEdit = async (row: EmailTemplateReference) => {
        setCurrent(row);
        setEditFile(null);
        setEditImageFiles([]);
        setEditHtmlBody('');
        editForm.setFieldsValue({
            contentName: row.contentName,
            businessDomainCode: row.businessDomainCode,
            ownerId: row.ownerId,
        });
        setEditVisible(true);
        loadOwnerOptions();
        if (row.versionId) {
            setLoadingPreview(true);
            try {
                const version = await v2Api.emailContentPreview(row.contentId, row.versionId, 'desktop');
                editForm.setFieldsValue({
                    subjectText: version.subjectText,
                    previewText: version.previewText,
                    senderDisplayName: version.senderDisplayName,
                    replyToAddress: version.replyToAddress,
                    validUntil: dateTimeLocalValue(version.validUntil),
                });
                setEditHtmlBody(version.previewContent || '');
            } catch (error) {
                message.error(asV2Error(error).message);
                setEditVisible(false);
            } finally {
                setLoadingPreview(false);
            }
        }
    };

    const updateTemplate = async () => {
        if (!current) return;
        const values = await editForm.validateFields();
        if (current.versionId && !editHtmlBody.trim()) {
            message.error('HTML 正文不能为空');
            return;
        }
        setSaving(true);
        try {
            const prepared = current.versionId
                ? await materializeHtmlImages(
                    editHtmlBody,
                    editImageFiles,
                    async (image) => (await v2Api.uploadEmailTemplateImage(image)).url
                )
                : undefined;
            const result = await v2Api.updateEmailContent(current.contentId, {
                contentName: values.contentName,
                businessDomainCode: values.businessDomainCode,
                ownerId: Number(values.ownerId),
                version: Number(current.version || 0),
            });
            let next = { ...current, ...result };
            if (current.versionId && prepared) {
                const metadata = {
                    htmlBody: prepared.html,
                    subjectText: values.subjectText,
                    previewText: values.previewText,
                    senderDisplayName: values.senderDisplayName,
                    replyToAddress: values.replyToAddress,
                    validUntil: toIsoDateTime(values.validUntil),
                };
                const version = await v2Api.createEmailContentVersion(current.contentId, metadata);
                next = { ...next, ...version };
                if (prepared.uploadedCount > 0) {
                    message.success(`已上传并替换 ${prepared.uploadedCount} 张 HTML 图片`);
                }
            }
            setCurrent(next);
            setEditVisible(false);
            setEditFile(null);
            setEditImageFiles([]);
            setEditHtmlBody('');
            message.success(current.versionId ? '模板已更新并生成新版本' : '模板信息已更新');
            await loadTemplates(templates.pageNo, templates.pageSize, keyword);
        } catch (error) {
            message.error(asV2Error(error).message);
        } finally {
            setSaving(false);
        }
    };

    const uploadEditorImage = async (image: File) => {
        try {
            const result = await v2Api.uploadEmailTemplateImage(image);
            message.success('图片已上传并插入邮件正文');
            return result.url;
        } catch (error) {
            message.error(asV2Error(error).message);
            throw error;
        }
    };

    const archiveTemplate = (row: EmailTemplateReference) => {
        Modal.confirm({
            title: `删除模板“${row.contentName || `#${row.contentId}`}”？`,
            content: '删除后模板将从列表中停用，历史版本和审计记录仍会保留。',
            okText: '确认删除',
            cancelText: '取消',
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    await v2Api.archiveEmailContent(row.contentId, {
                        version: Number(row.version || 0),
                        reason: '从邮件模板管理页面删除（软删除）',
                    });
                    message.success('模板已删除');
                    const targetPage = templates.items.length === 1 && templates.pageNo > 1
                        ? templates.pageNo - 1
                        : templates.pageNo;
                    await loadTemplates(targetPage, templates.pageSize, keyword);
                } catch (error) {
                    message.error(asV2Error(error).message);
                    throw error;
                }
            },
        });
    };

    const loadTestRecipients = async () => {
        const recipients = await v2Api.emailTestRecipients();
        setTestRecipients(recipients || []);
        return recipients || [];
    };

    const openTestRecipientConfig = async () => {
        setSaving(true);
        try {
            await loadTestRecipients();
            testRecipientForm.setFieldsValue({ recipients: [{ email: '' }] });
            setTestRecipientVisible(true);
        } catch (error) {
            message.error(asV2Error(error).message);
        } finally {
            setSaving(false);
        }
    };

    const saveTestRecipients = async () => {
        const values = await testRecipientForm.validateFields();
        const recipients = (values.recipients || []).filter((item: any) => item && item.email);
        if (!recipients.length) {
            setTestRecipientVisible(false);
            return;
        }
        setSaving(true);
        try {
            setTestRecipients(await v2Api.addEmailTestRecipients(recipients));
            testRecipientForm.setFieldsValue({ recipients: [{ email: '' }] });
            message.success('测试邮箱已保存');
        } catch (error) {
            message.error(asV2Error(error).message);
        } finally {
            setSaving(false);
        }
    };

    const removeTestRecipient = async (recipientId: number) => {
        setSaving(true);
        try {
            await v2Api.removeEmailTestRecipient(recipientId);
            await loadTestRecipients();
            message.success('测试邮箱已移除');
        } catch (error) {
            message.error(asV2Error(error).message);
        } finally {
            setSaving(false);
        }
    };

    const openTestSend = async (row: EmailTemplateReference) => {
        if (!row.versionId) return;
        setCurrent(row);
        setSaving(true);
        try {
            const [recipients, accounts] = await Promise.all([
                loadTestRecipients(),
                v2Api.activeMailAccountOptions(),
            ]);
            setTestMailAccounts(accounts || []);
            testSendForm.resetFields();
            testSendForm.setFieldsValue({
                recipientIds: recipients.map((item) => item.id),
                mailAccountId: accounts.length === 1 ? accounts[0].id : undefined,
            });
            setTestSendVisible(true);
        } catch (error) {
            message.error(asV2Error(error).message);
        } finally {
            setSaving(false);
        }
    };

    const sendTestMail = async () => {
        if (!current?.versionId) return;
        const values = await testSendForm.validateFields();
        setSaving(true);
        try {
            const result = await v2Api.sendEmailTemplateTest(current.contentId, current.versionId, {
                mailAccountId: Number(values.mailAccountId),
                recipientIds: values.recipientIds.map(Number),
            });
            if (result.testStatus === 'PASSED') {
                message.success(`测试邮件已发送：${result.sentCount}/${result.totalCount}`);
            } else {
                message.warning(
                    `测试发送完成：成功 ${result.sentCount}，失败 ${result.failedCount}，结果未知 ${result.unknownCount}`
                );
            }
            setTestSendVisible(false);
            await loadTemplates(templates.pageNo, templates.pageSize, keyword);
        } catch (error) {
            message.error(asV2Error(error).message);
        } finally {
            setSaving(false);
        }
    };

    const columns = [
        {
            title: '模板',
            key: 'template',
            render: (_: any, row: EmailTemplateReference) => (
                <div>
                    <strong>{row.contentName || `模板 #${row.contentId}`}</strong>
                    <span className="v2-subline">内容 ID：{row.contentId}</span>
                </div>
            ),
        },
        {
            title: '负责人',
            key: 'owner',
            width: 160,
            render: (_: any, row: EmailTemplateReference) => (
                <div>
                    <span>{row.ownerName || row.ownerAccount || `#${row.ownerId}`}</span>
                    {row.ownerName && row.ownerAccount && <span className="v2-subline">{row.ownerAccount}</span>}
                </div>
            ),
        },
        {
            title: '版本',
            key: 'version',
            width: 100,
            render: (_: any, row: EmailTemplateReference) =>
                row.versionId ? `V${row.versionNo || '?'} / #${row.versionId}` : '未上传',
        },
        {
            title: '安全检查',
            dataIndex: 'analysisStatus',
            width: 120,
            render: (value: string) => (
                <Tag color={value === 'READY' ? 'green' : value === 'BLOCKED' ? 'red' : 'default'}>
                    {templateAnalysisStatusLabel(value)}
                </Tag>
            ),
        },
        {
            title: '测试状态',
            dataIndex: 'testStatus',
            width: 110,
            render: (value: string) => (
                <Tag color={value === 'PASSED' ? 'green' : value === 'FAILED' ? 'red' : 'default'}>
                    {templateTestStatusLabel(value)}
                </Tag>
            ),
        },
        {
            title: '操作',
            key: 'actions',
            width: 430,
            render: (_: any, row: EmailTemplateReference) => (
                <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)}>
                        编辑
                    </Button>
                    <Button
                        size="small"
                        icon={<UploadOutlined />}
                        onClick={() => {
                            setCurrent(row);
                            setFile(null);
                            setUploadImageFiles([]);
                            setUploadHtmlBody('');
                            uploadForm.resetFields();
                            setUploadVisible(true);
                        }}
                    >
                        新建版本
                    </Button>
                    <Button
                        size="small"
                        icon={<EyeOutlined />}
                        disabled={!row.versionId}
                        onClick={() => openPreview(row, 'desktop')}
                    >
                        预览
                    </Button>
                    <Button
                        size="small"
                        icon={<MailOutlined />}
                        disabled={!row.versionId || !['READY', 'LEGACY_ACCEPTED'].includes(row.analysisStatus || '')}
                        onClick={() => openTestSend(row)}
                    >
                        发送测试邮件
                    </Button>
                    <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => archiveTemplate(row)}
                    >
                        删除
                    </Button>
                </Space>
            ),
        },
    ];

    const report = sanitizationReport(preview);
    return (
        <div className="v2-page mail-page">
            <PageHeader
                eyebrow="开发与培育 · 批量邮件"
                title="邮件内容模板"
                extra={
                    <Space>
                        <Button icon={<MailOutlined />} loading={saving} onClick={openTestRecipientConfig}>
                            测试邮箱配置
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                createForm.resetFields();
                                if (currentActorId) createForm.setFieldsValue({ ownerId: currentActorId });
                                setCreateVisible(true);
                                loadOwnerOptions();
                            }}
                        >
                            新建模板
                        </Button>
                    </Space>
                }
            />
            <Card className="v2-panel" bordered={false}>
                <Space style={{ marginBottom: 16 }}>
                    <span>负责人</span>
                    <Select
                        showSearch
                        optionFilterProp="children"
                        value={selectedOwnerId}
                        disabled={!canViewAllOwners}
                        loading={loadingOwners}
                        style={{ width: 220 }}
                        placeholder="请选择负责人"
                        onChange={(ownerId: number) => {
                            setSelectedOwnerId(ownerId);
                            setListLoading(true);
                            v2Api.emailContents({
                                ownerId,
                                keyword: keyword.trim() || undefined,
                                pageNo: 1,
                                pageSize: templates.pageSize,
                            }).then(setTemplates)
                                .catch((error) => message.error(asV2Error(error).message))
                                .finally(() => setListLoading(false));
                        }}
                    >
                        {ownerOptions.map((owner) => (
                            <Select.Option key={owner.id} value={owner.id}>
                                {owner.realName || owner.account}（{owner.account}）
                            </Select.Option>
                        ))}
                    </Select>
                    <Input.Search
                        allowClear
                        placeholder="按模板名称或业务域搜索"
                        style={{ width: 320 }}
                        onSearch={(value) => {
                            setKeyword(value);
                            loadTemplates(1, templates.pageSize, value);
                        }}
                    />
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => loadTemplates(templates.pageNo, templates.pageSize, keyword)}
                    >
                        刷新
                    </Button>
                </Space>
                <Table
                    rowKey="contentId"
                    columns={columns}
                    dataSource={templates.items}
                    loading={listLoading}
                    pagination={{
                        current: templates.pageNo,
                        pageSize: templates.pageSize,
                        total: templates.total,
                        showSizeChanger: true,
                        pageSizeOptions: MAIL_PAGE_SIZE_OPTIONS,
                        showTotal: (total) => `共 ${total} 个模板`,
                        onChange: (pageNo, pageSize) => {
                            rememberMailPageSize('templates', pageSize);
                            loadTemplates(pageNo, pageSize, keyword);
                        },
                    }}
                    locale={{ emptyText: '暂无模板，可先新建模板' }}
                    scroll={{ x: 1080 }}
                />
            </Card>

            <Modal
                title="新建邮件模板"
                visible={createVisible}
                onOk={createTemplate}
                confirmLoading={saving}
                onCancel={() => setCreateVisible(false)}
                okText="创建并编辑内容"
            >
                <Form form={createForm} layout="vertical">
                    <Form.Item name="contentName" label="模板名称" rules={[{ required: true, whitespace: true }]}>
                        <Input maxLength={200} />
                    </Form.Item>
                    <Form.Item name="businessDomainCode" label="业务域编码">
                        <Input maxLength={40} />
                    </Form.Item>
                    <Form.Item name="ownerId" label="负责人" rules={[{ required: true, message: '请选择负责人' }]}>
                        <Select
                            showSearch
                            allowClear
                            disabled={!canViewAllOwners}
                            loading={loadingOwners}
                            optionFilterProp="children"
                            placeholder="请选择销售或管理员"
                            notFoundContent={loadingOwners ? '正在加载…' : '暂无可选负责人'}
                        >
                            {ownerOptions.map((owner) => (
                                <Select.Option key={owner.id} value={owner.id}>
                                    {`${owner.realName || owner.account}（${owner.account}）${ownerRoleLabel(owner.roleCodes) ? ` · ${ownerRoleLabel(owner.roleCodes)}` : ''}`}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    {ownerError && (
                        <Alert
                            type="warning"
                            showIcon
                            message={ownerError}
                            description={<Button size="small" onClick={loadOwnerOptions}>重新加载</Button>}
                        />
                    )}
                </Form>
            </Modal>

            <Modal
                title={`编辑邮件模板${current ? ` · #${current.contentId}` : ''}`}
                visible={editVisible}
                onOk={updateTemplate}
                confirmLoading={saving}
                onCancel={() => {
                    setEditVisible(false);
                    setEditFile(null);
                    setEditImageFiles([]);
                    setEditHtmlBody('');
                }}
                okText="保存"
                width={980}
                bodyStyle={{ maxHeight: '78vh', overflowY: 'auto' }}
            >
                <Form form={editForm} layout="vertical">
                    <Form.Item
                        name="contentName"
                        label="模板名称"
                        rules={[{ required: true, whitespace: true }]}
                    >
                        <Input maxLength={200} />
                    </Form.Item>
                    <Form.Item name="businessDomainCode" label="业务域编码">
                        <Input maxLength={40} />
                    </Form.Item>
                    <Form.Item
                        name="ownerId"
                        label="负责人"
                        rules={[{ required: true, message: '请选择负责人' }]}
                    >
                        <Select
                            showSearch
                            disabled={!canViewAllOwners}
                            loading={loadingOwners}
                            optionFilterProp="children"
                            placeholder="请选择销售或管理员"
                            notFoundContent={loadingOwners ? '正在加载…' : '暂无可选负责人'}
                        >
                            {ownerOptions.map((owner) => (
                                <Select.Option key={owner.id} value={owner.id}>
                                    {`${owner.realName || owner.account}（${owner.account}）${ownerRoleLabel(owner.roleCodes) ? ` · ${ownerRoleLabel(owner.roleCodes)}` : ''}`}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    {ownerError && <Alert type="warning" showIcon message={ownerError} />}
                    {current?.versionId && (
                        <>
                            <Form.Item
                                name="subjectText"
                                label="邮件主题"
                                rules={[{ required: true, whitespace: true }]}
                            >
                                <Input maxLength={500} />
                            </Form.Item>
                            <Form.Item name="previewText" label="预览摘要">
                                <Input maxLength={500} />
                            </Form.Item>
                            <Form.Item
                                name="senderDisplayName"
                                label="发件人显示名"
                                rules={[{ required: true, whitespace: true }]}
                            >
                                <Input maxLength={200} />
                            </Form.Item>
                            <Form.Item
                                name="replyToAddress"
                                label="Reply-To 邮箱"
                                rules={[{ required: true }, { type: 'email', message: '请输入合法邮箱地址' }]}
                            >
                                <Input maxLength={320} />
                            </Form.Item>
                            <Form.Item name="validUntil" label="有效期">
                                <Input type="datetime-local" />
                            </Form.Item>
                            <Form.Item
                                label="HTML 文件"
                                extra="可重新上传 HTML 并继续在下方编辑；保存时会生成新版本并执行安全检查"
                            >
                                <Upload
                                    accept=".html,.htm,text/html"
                                    fileList={editFile ? ([editFile] as any) : []}
                                    beforeUpload={(nextFile) => {
                                        setEditFile(nextFile as File);
                                        readHtmlFile(nextFile as File)
                                            .then(setEditHtmlBody)
                                            .catch((error) => message.error(error.message));
                                        return false;
                                    }}
                                    onRemove={() => {
                                        setEditFile(null);
                                        return true;
                                    }}
                                >
                                    <Button icon={<FileAddOutlined />}>选择新的 HTML</Button>
                                </Upload>
                            </Form.Item>
                            <Form.Item
                                label="HTML 引用的本地图片"
                                extra="当正文包含 images/logo.png、file:// 等本地路径时，请选择对应图片；点击保存后会自动上传并替换链接"
                            >
                                <Upload
                                    multiple
                                    accept="image/png,image/jpeg,image/gif"
                                    fileList={editImageFiles as any}
                                    beforeUpload={(nextFile) => {
                                        setEditImageFiles((previous) => [
                                            ...previous.filter((candidate) => candidate.name !== nextFile.name),
                                            nextFile as File,
                                        ]);
                                        return false;
                                    }}
                                    onRemove={(removed) => {
                                        const file = removed as unknown as File;
                                        setEditImageFiles((previous) => previous.filter((candidate) =>
                                            candidate.name !== file.name
                                            || candidate.size !== file.size
                                            || candidate.lastModified !== file.lastModified));
                                        return true;
                                    }}
                                >
                                    <Button icon={<UploadOutlined />}>选择关联图片（可多选）</Button>
                                </Upload>
                            </Form.Item>
                            <Form.Item label="HTML 正文" required>
                                <MailHtmlEditor
                                    value={editHtmlBody}
                                    onChange={setEditHtmlBody}
                                    onUploadImage={uploadEditorImage}
                                    disabled={saving || loadingPreview}
                                />
                            </Form.Item>
                        </>
                    )}
                    {loadingPreview && <Alert type="info" message="正在加载当前版本信息…" />}
                </Form>
            </Modal>

            <Modal
                title={`新建 HTML 版本${current ? ` · 模板 #${current.contentId}` : ''}`}
                visible={uploadVisible}
                onOk={uploadHtml}
                confirmLoading={saving}
                onCancel={() => {
                    setUploadVisible(false);
                    setFile(null);
                    setUploadImageFiles([]);
                    setUploadHtmlBody('');
                }}
                okText="保存为新版本"
                width={980}
                bodyStyle={{ maxHeight: '78vh', overflowY: 'auto' }}
            >
                <Form form={uploadForm} layout="vertical">
                    <Form.Item
                        label="HTML 文件"
                        extra="可上传已有 HTML，也可以直接使用下方富文本编辑器创建内容"
                    >
                        <Upload
                            accept=".html,.htm,text/html"
                            fileList={file ? ([file] as any) : []}
                            beforeUpload={(nextFile) => {
                                setFile(nextFile as File);
                                readHtmlFile(nextFile as File)
                                    .then(setUploadHtmlBody)
                                    .catch((error) => message.error(error.message));
                                return false;
                            }}
                            onRemove={() => {
                                setFile(null);
                                return true;
                            }}
                        >
                            <Button icon={<FileAddOutlined />}>选择 UTF-8 HTML</Button>
                        </Upload>
                    </Form.Item>
                    <Form.Item
                        label="HTML 引用的本地图片"
                        extra="内嵌 Base64 图片会自动处理；本地路径图片请在这里选择，保存时会上传并替换为公开链接"
                    >
                        <Upload
                            multiple
                            accept="image/png,image/jpeg,image/gif"
                            fileList={uploadImageFiles as any}
                            beforeUpload={(nextFile) => {
                                setUploadImageFiles((previous) => [
                                    ...previous.filter((candidate) => candidate.name !== nextFile.name),
                                    nextFile as File,
                                ]);
                                return false;
                            }}
                            onRemove={(removed) => {
                                const file = removed as unknown as File;
                                setUploadImageFiles((previous) => previous.filter((candidate) =>
                                    candidate.name !== file.name
                                    || candidate.size !== file.size
                                    || candidate.lastModified !== file.lastModified));
                                return true;
                            }}
                        >
                            <Button icon={<UploadOutlined />}>选择关联图片（可多选）</Button>
                        </Upload>
                    </Form.Item>
                    <Form.Item label="HTML 正文" required>
                        <MailHtmlEditor
                            value={uploadHtmlBody}
                            onChange={setUploadHtmlBody}
                            onUploadImage={uploadEditorImage}
                            disabled={saving}
                        />
                    </Form.Item>
                    <Form.Item name="subjectText" label="邮件主题" rules={[{ required: true, whitespace: true }]}>
                        <Input maxLength={500} />
                    </Form.Item>
                    <Form.Item name="previewText" label="预览摘要">
                        <Input maxLength={500} />
                    </Form.Item>
                    <Form.Item name="senderDisplayName" label="发件人显示名" rules={[{ required: true }]}>
                        <Input maxLength={200} />
                    </Form.Item>
                    <Form.Item
                        name="replyToAddress"
                        label="Reply-To 邮箱"
                        rules={[{ required: true }, { type: 'email', message: '请输入合法邮箱地址' }]}
                    >
                        <Input maxLength={320} />
                    </Form.Item>
                    <Form.Item name="validUntil" label="有效期">
                        <Input type="datetime-local" />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="测试邮箱配置"
                visible={testRecipientVisible}
                onOk={saveTestRecipients}
                confirmLoading={saving}
                onCancel={() => setTestRecipientVisible(false)}
                okText="保存新增邮箱"
                width={620}
            >
                {testRecipients.length > 0 && (
                    <Card size="small" title="已配置邮箱" style={{ marginBottom: 16 }}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            {testRecipients.map((recipient) => (
                                <Space key={recipient.id} style={{ justifyContent: 'space-between', width: '100%' }}>
                                    <span>{recipient.recipientMasked}</span>
                                    <Button
                                        size="small"
                                        danger
                                        disabled={saving}
                                        onClick={() => removeTestRecipient(recipient.id)}
                                    >
                                        移除
                                    </Button>
                                </Space>
                            ))}
                        </Space>
                    </Card>
                )}
                <Form form={testRecipientForm} layout="vertical">
                    <Form.List name="recipients">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map((field) => (
                                    <Space key={field.key} align="start" style={{ width: '100%' }}>
                                        <Form.Item
                                            {...field}
                                            name={[field.name, 'email']}
                                            fieldKey={[field.fieldKey, 'email']}
                                            label="测试邮箱"
                                            rules={[
                                                { required: true, message: '请输入测试邮箱' },
                                                { type: 'email', message: '请输入合法邮箱地址' },
                                            ]}
                                        >
                                            <Input style={{ width: 440 }} maxLength={320} />
                                        </Form.Item>
                                        <Form.Item label=" " colon={false} style={{ marginBottom: 0 }}>
                                            <Button
                                                type="link"
                                                danger
                                                icon={<MinusCircleOutlined />}
                                                aria-label="删除测试邮箱"
                                                onClick={() => remove(field.name)}
                                            />
                                        </Form.Item>
                                    </Space>
                                ))}
                                <Button
                                    type="dashed"
                                    block
                                    icon={<PlusOutlined />}
                                    disabled={fields.length >= 10}
                                    onClick={() => add({ email: '' })}
                                >
                                    增加测试邮箱
                                </Button>
                            </>
                        )}
                    </Form.List>
                </Form>
            </Modal>

            <Modal
                title={`发送测试邮件${current ? ` · ${current.contentName || `模板 #${current.contentId}`}` : ''}`}
                visible={testSendVisible}
                onOk={sendTestMail}
                confirmLoading={saving}
                onCancel={() => setTestSendVisible(false)}
                okText="确认发送"
            >
                <Form form={testSendForm} layout="vertical">
                    <Form.Item name="mailAccountId" label="发件邮箱" rules={[{ required: true, message: '请选择发件邮箱' }]}>
                        <Select showSearch optionFilterProp="children" placeholder="请选择已启用邮箱账号">
                            {testMailAccounts.map((account) => (
                                <Select.Option key={account.id} value={account.id}>
                                    {account.senderDisplayName || account.providerAccountRef}（{account.fromAddress}）
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="recipientIds"
                        label="测试收件人"
                        rules={[{ required: true, type: 'array', min: 1, message: '请选择至少一个测试邮箱' }]}
                    >
                        <Select mode="multiple" placeholder="请先通过测试邮箱配置添加地址">
                            {testRecipients.map((recipient) => (
                                <Select.Option key={recipient.id} value={recipient.id}>
                                    {recipient.recipientMasked}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    {!testRecipients.length && (
                        <Alert type="warning" showIcon message="尚未配置测试邮箱，请先关闭窗口并完成测试邮箱配置" />
                    )}
                </Form>
            </Modal>

            <Drawer
                title="安全预览"
                width="min(920px, 96vw)"
                visible={previewVisible}
                onClose={() => setPreviewVisible(false)}
                destroyOnClose
            >
                <Space className="mail-preview-toolbar">
                    <Radio.Group value={mode} onChange={(event) => changePreviewMode(event.target.value)}>
                        <Radio.Button value="desktop">桌面</Radio.Button>
                        <Radio.Button value="mobile">手机</Radio.Button>
                        <Radio.Button value="text">纯文本</Radio.Button>
                    </Radio.Group>
                    {mode !== 'text' && (
                        <Button onClick={() => setLoadRemoteImages((value) => !value)}>
                            {loadRemoteImages ? '阻止远程图片' : '加载远程图片'}
                        </Button>
                    )}
                    {preview && (
                        <Tag color={preview.analysisStatus === 'READY' ? 'green' : 'red'}>
                            {templateAnalysisStatusLabel(preview.analysisStatus)}
                        </Tag>
                    )}
                </Space>
                {loadingPreview ? (
                    <Card loading />
                ) : preview ? (
                    <>
                        <Descriptions size="small" column={1} bordered>
                            <Descriptions.Item label="主题">{preview.subjectText || '—'}</Descriptions.Item>
                            <Descriptions.Item label="摘要">{preview.previewText || '—'}</Descriptions.Item>
                            <Descriptions.Item label="有效期">{formatDateTime(preview.validUntil)}</Descriptions.Item>
                        </Descriptions>
                        {(report?.blockers || preview.blockers || []).length > 0 && (
                            <Alert
                                className="mail-preview-report"
                                type="error"
                                showIcon
                                message="存在发布阻断项"
                                description={(report?.blockers || preview.blockers || [])
                                    .map(templateCheckMessage)
                                    .join('；')}
                            />
                        )}
                        {(report?.warnings || preview.warnings || []).length > 0 && (
                            <Alert
                                className="mail-preview-report"
                                type="warning"
                                showIcon
                                message="检查提示"
                                description={(report?.warnings || preview.warnings || [])
                                    .map(templateCheckMessage)
                                    .join('；')}
                            />
                        )}
                        <div
                            className={`mail-preview-shell is-${mode}`}
                            style={{ maxWidth: mode === 'mobile' ? 375 : 680 }}
                        >
                            {mode === 'text' ? (
                                <pre>{preview.previewContent || ''}</pre>
                            ) : (
                                <iframe
                                    title="邮件模板安全预览"
                                    sandbox=""
                                    referrerPolicy="no-referrer"
                                    srcDoc={
                                        loadRemoteImages
                                            ? preview.previewContent || ''
                                            : blockRemoteImages(preview.previewContent || '')
                                    }
                                />
                            )}
                        </div>
                    </>
                ) : (
                    <Alert type="warning" message="未能读取预览内容" />
                )}
            </Drawer>
        </div>
    );
};

export default EmailTemplatesPage;
